/**
 * Base class for all Web Components in FireGuard
 * Provides common lifecycle methods and utilities
 */
export abstract class BaseComponent extends HTMLElement {
  protected shadow: ShadowRoot;
  protected isConnected = false;

  constructor() {
    super();
    this.shadow = this.attachShadow({ mode: 'open' });
  }

  /**
   * Called when component is added to DOM
   */
  connectedCallback() {
    this.isConnected = true;
    this.render();
  }

  /**
   * Called when component is removed from DOM
   */
  disconnectedCallback() {
    this.isConnected = false;
    this.cleanup();
  }

  /**
   * Abstract method - must be implemented by subclasses
   */
  protected abstract render(): void;

  /**
   * Optional cleanup method
   */
  protected cleanup(): void {
    // Override in subclasses if needed
  }

  /**
   * Helper to create elements with attributes
   */
  protected createElement<K extends keyof HTMLElementTagNameMap>(
    tag: K,
    attrs?: Record<string, string | boolean | number>,
    text?: string
  ): HTMLElementTagNameMap[K] {
    const el = document.createElement(tag);
    if (attrs) {
      Object.entries(attrs).forEach(([key, value]) => {
        if (key === 'className') {
          el.className = String(value);
        } else if (key === 'htmlFor') {
          (el as unknown as HTMLLabelElement).htmlFor = String(value);
        } else if (key === 'textContent') {
          // Many pages pass text this way; it must become content, not an attribute.
          el.textContent = String(value);
        } else if (typeof value === 'boolean') {
          el.toggleAttribute(key, value);
        } else {
          el.setAttribute(key, String(value));
        }
      });
    }
    if (text) {
      el.textContent = text;
    }
    return el;
  }

  /**
   * Helper to emit custom events
   */
  protected emit(eventName: string, detail?: unknown): void {
    this.dispatchEvent(
      new CustomEvent(eventName, {
        detail,
        bubbles: true,
        composed: true,
      })
    );
  }

  /**
   * Helper to add global styles to shadow DOM
   */
  protected addStyles(css: string): void {
    const style = document.createElement('style');
    style.textContent = css;
    this.shadow.appendChild(style);
  }

  /**
   * Common styles shared across components
   */
  protected getCommonStyles(): string {
    return `
      :host {
        --primary: #3b82f6;
        --primary-dark: #2563eb;
        --gray-50: #f9fafb;
        --gray-100: #f3f4f6;
        --gray-200: #e5e7eb;
        --gray-300: #d1d5db;
        --gray-500: #6b7280;
        --gray-700: #374151;
        --gray-900: #111827;
        --danger: #ef4444;
        --success: #10b981;
        --spacing-sm: 8px;
        --spacing-md: 16px;
        --spacing-lg: 24px;
        --border-radius: 8px;
      }

      * {
        box-sizing: border-box;
        margin: 0;
        padding: 0;
      }

      button {
        font-family: inherit;
        cursor: pointer;
      }

      input, textarea, select {
        font-family: inherit;
        font-size: inherit;
      }

      img, svg, video, canvas { max-width: 100%; }

      /* Phone-width rules shared by every page: the header nav scrolls
         sideways instead of pushing the page wider, and flex rows wrap. */
      @media (max-width: 640px) {
        .header-content { flex-wrap: wrap; gap: var(--spacing-sm); }
        .nav { width: 100%; overflow-x: auto; -webkit-overflow-scrolling: touch; gap: var(--spacing-sm); padding-bottom: 4px; scrollbar-width: none; }
        .nav::-webkit-scrollbar { display: none; }
        .nav > * { flex: 0 0 auto; }
        .title-bar, .page-header, .section-header, .actions, .action-buttons, .form-row, .button-group, .form-buttons { flex-wrap: wrap; }
        table { display: block; max-width: 100%; overflow-x: auto; }
      }
    `;
  }
}
